import React, { useEffect, useState, useRef, useCallback } from 'react'
import styles from './EditConnectionModal.module.sass'
import { useForm, FormProvider } from 'react-hook-form'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import HFDropdown from '../../components/HFInputs/HFDropdown'
import { zodResolver } from '@hookform/resolvers/zod'
import { EditConnectionModalSchema } from './EditConnectionModalSchema'
import { useParams } from 'react-router-dom'
import { useFetchList } from '../../store'
import { Appointment } from '../../models'
import ActionButton from '@jimmyboys/liita-ui/dist/components/ActionButton'
import { API_URL } from '../../store/fetchUtils'
import TaskHeaderErrors from '../../components/TaskHeaderErrors/TaskHeaderErrors'
import { flatHookFormErrors } from 'controllers/TaskManager/controllers/utils'
import { AppointmentStatus } from '../../utils/enums'

const excludedDisabilityCodes = [
	'AAD',
	'AAT',
	'ACCD',
	'ACSD',
	'ADM',
	'AINC',
	'APD',
]

const EditConnectionModal = ({ onClose, tableData, initialAppointmentId }) => {
	const { i18n } = useGlobal()
	const [error, setError] = useState(null)

	const dragRef = useRef({
		isDragging: false,
		startX: 0,
		startY: 0,
		initX: 0,
		initY: 0,
	})
	const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 })

	const handleDragStart = useCallback(
		(e) => {
			if (e.button !== 0) return
			if (
				e.target.closest(
					'button, input, select, textarea, a, .dropdown'
				)
			)
				return
			e.preventDefault()
			const ref = dragRef.current
			ref.isDragging = true
			ref.startX = e.clientX
			ref.startY = e.clientY
			ref.initX = dragOffset.x
			ref.initY = dragOffset.y
			const onMove = (e) => {
				if (!ref.isDragging) return
				setDragOffset({
					x: ref.initX + (e.clientX - ref.startX),
					y: ref.initY + (e.clientY - ref.startY),
				})
			}
			const onUp = () => {
				ref.isDragging = false
				ref.initX = ref.initX + (e.clientX - ref.startX)
				ref.initY = ref.initY + (e.clientY - ref.startY)
				document.removeEventListener('mousemove', onMove)
				document.removeEventListener('mouseup', onUp)
			}
			document.addEventListener('mousemove', onMove)
			document.addEventListener('mouseup', onUp)
		},
		[dragOffset.x, dragOffset.y]
	)

	const { clinicalProcessId } = useParams()
	const appointmentsUri = `/clinical_processes/${clinicalProcessId}/appointments`
	const { contents } = useFetchList([appointmentsUri], [Appointment.fromJSON])

	const methods = useForm({
		mode: 'onSubmit',
		resolver: zodResolver(EditConnectionModalSchema),
		shouldUnregister: true,
		defaultValues: {
			appointmentToEdit: initialAppointmentId
				? String(initialAppointmentId)
				: '',
		},
	})

	const appointmentToEdit = methods.watch('appointmentToEdit')
	const newNext = methods.watch('newNext')

	const [appointmentsData = []] = contents || []
	const allowedNextAppointmentStatusIds = [1, 2, 3, 5, 6, 9]
	const filteredAppointments = appointmentsData.filter((app) =>
		allowedNextAppointmentStatusIds.includes(app.status?.id)
	)
	const validAppointments = appointmentsData.filter(
		(appointment) =>
			appointment.status?.id === AppointmentStatus.Executed &&
			!excludedDisabilityCodes.includes(appointment.disability?.id)
	)
	const appointmentOptions = [
		{ text: '', value: '' },
		...validAppointments
			.map((appointment) => ({
				text: `${appointment.id} - ${
					appointment.date?.shortDateFormat ?? ''
				} – ${appointment.medicalSpecialty?.name ?? ''}`,
				value: String(appointment.id),
			}))
			.sort((a, b) => a.text.localeCompare(b.text)),
	]
	const selectedAppointment = tableData?.find(
		(app) => String(app.Id) === String(appointmentToEdit)
	)
	const currentNextId = selectedAppointment?.IdNextAppointment || null
	const nextAppointment = currentNextId
		? tableData?.find((app) => String(app.Id) === String(currentNextId))
		: null
	const currentNextText = currentNextId
		? `${currentNextId} – ${nextAppointment?.Date ?? ''} – ${
				nextAppointment?.Specialty ?? ''
		  }`
		: ''

	const validAppointmentsConnectionsOptions = filteredAppointments.filter(
		(appointment) =>
			String(appointment.id) !== String(appointmentToEdit) &&
			String(appointment.id) !== String(currentNextId)
	)
	const appointmentConnectionsOptions = [
		{ text: '', value: '' },
		...validAppointmentsConnectionsOptions
			.map((appointment) => ({
				text: `${String(appointment.id)} - ${
					appointment.date.shortDateFormat
				} – ${appointment.medicalSpecialty.name}`,
				value: String(appointment.id),
			}))
			.sort((a, b) => a.text.localeCompare(b.text)),
	]

	const fieldLabels = {
		appointmentToEdit: i18n`Appointment to Edit`,
		newNext: i18n`New Next Appointment`,
	}

	const { handleSubmit, formState } = methods
	const { isSubmitting } = formState

	const onSubmit = async (data) => {
		const { appointmentToEdit, newNext } = data

		if (!newNext) return

		const payload = {
			appointmentId: Number(appointmentToEdit),
			nextAppointmentId: Number(newNext),
		}

		try {
			const token =
				localStorage.getItem('session-token') ||
				sessionStorage.getItem('session-token')
			const response = await fetch(
				`${API_URL}/api/clinical_processes/${clinicalProcessId}/appointments/update_connection`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						Authorization: `Bearer ${token}`,
					},
					body: JSON.stringify(payload),
				}
			)
			const result = await response.json()

			const resultData =
				result?.['soap:Envelope']?.['soap:Body']?.[
					'AtualizarLigacaoConsultaResponse'
				]?.['AtualizarLigacaoConsultaResult']

			const sucesso = resultData?.['Sucesso']?._text === 'true'
			const mensagem = resultData?.['Mensagem']?._text

			if (sucesso) {
				onClose(true)
				return
			}

			setError([mensagem])
		} catch (err) {
			console.error('Erro inesperado:', err)
			setError(['Erro ao comunicar com o servidor. Tente novamente.'])
		}
	}

	useEffect(() => {
		const flattenErrors = flatHookFormErrors(formState.errors)
		if (flattenErrors) {
			const errorMessages = Object.keys(flattenErrors).map((errorKey) => {
				const errorMessage = flattenErrors[errorKey]
				const splittedErrorKey = errorKey.split('.')
				const field = splittedErrorKey[splittedErrorKey.length - 2]
				const fieldLabel = fieldLabels[field] ?? field
				return `${fieldLabel}: ${errorMessage}`
			})
			setError(errorMessages)
		}
	}, [formState.errors])

	const modalStyle = {
		transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)`,
	}

	return (
		<FormProvider {...methods}>
			<form id={'form_edit_connection'} onSubmit={handleSubmit(onSubmit)}>
				<div className={styles.backdrop}>
					<div className={styles.modal} style={modalStyle}>
						<div
							style={{ cursor: 'move', marginBottom: '8px' }}
							onMouseDown={handleDragStart}
						>
							{error && <TaskHeaderErrors errors={error ?? []} />}
							<h3>{i18n`Edit Manual Connection`}</h3>
						</div>
						<fieldset>
							<div className={styles.row}>
								<label>{i18n`Appointment to Edit`}</label>
								<HFDropdown
									name='appointmentToEdit'
									options={appointmentOptions}
									optionsAlign='right'
									displayValue={false}
									searchable={true}
									noData={i18n`No results`}
								/>
							</div>

							<div className={styles.row}>
								<label>{i18n`Next Current Appointment`}</label>
								<span
									className={
										currentNextText
											? styles.readonlyDisplay
											: styles.readonlyDisplayEmpty
									}
								>
									{appointmentToEdit
										? currentNextText ||
										  i18n`No next appointment linked`
										: ''}
								</span>
							</div>

							{appointmentToEdit && (
								<div className={styles.row}>
									<label>{i18n`New Next Appointment`}</label>
									<HFDropdown
										name='newNext'
										options={appointmentConnectionsOptions}
										optionsAlign='right'
										displayValue={false}
										searchable={true}
										noData={i18n`No results`}
									/>
								</div>
							)}

							<menu className={styles.actions}>
								<ActionButton
									type='button'
									secondary
									onClick={() => onClose(false)}
								>
									<small>
										<strong>{i18n`Cancel`}</strong>
									</small>
								</ActionButton>

								<ActionButton
									form={`form_edit_connection`}
									type='submit'
									primary
								>
									<small>
										<strong>
											{isSubmitting
												? i18n`Submitting`
												: i18n`Send`}
										</strong>
									</small>
								</ActionButton>
							</menu>
						</fieldset>
					</div>
				</div>
			</form>
		</FormProvider>
	)
}

export default EditConnectionModal
